import { PRO_SUBSCRIPTION_PERKS, ROLE_NAMES, FREE_SUBSCRIPTION } from "@/constants";
import { useAppNavigation } from "@/features/Navigation/hooks/useAppNavigation";
import { usePermissions } from "@/features/Permissions/hooks/usePermissions";
import { UpsellModal } from "@/features/Subscriptions/components/UpsellModal/UpsellModal";
import { useSubscription } from "@/features/Subscriptions/hooks/useSubscription";
import { Illustrations } from "@/illustrations";
import { MenuDivider, useDisclosure } from "@chakra-ui/react";
import { Icon, MenuItem, TeamMenuItem, TeamSelector } from "@pufflig/prompt-studio-ui";
import { useSearchParams } from "react-router-dom";
import { usePendingInviteSubscription } from "../../hooks/usePendingInvites";
import { useTeamMemberSubscription } from "../../hooks/useTeamMembers";
import { useTeams, useTeamSubscription } from "../../hooks/useTeams";
import { TeamsModal } from "../TeamModal/TeamModal";
import { useEffect, useState } from "react";
import { useAnalytics } from "@/services/posthog/hooks";
import { goto_teams_event } from "@/services/posthog/events";

export const TeamNavigation = () => {
  const { onOpen: onOpenTeamsModal, onClose: onCloseTeamsModal, isOpen: isOpenTeamsModal } = useDisclosure();
  const { onOpen: onOpenUpsellModal, onClose: onCloseUpsellModal, isOpen: isOpenUpsellModal } = useDisclosure();
  const [searchParams] = useSearchParams();
  const { navigateToTeam, navigateToPersonalWorkspace } = useAppNavigation();
  const analytics = useAnalytics();

  const teamId = searchParams.get("team");

  useTeamSubscription();
  useTeamMemberSubscription();
  usePendingInviteSubscription();

  const { data: teams } = useTeams();
  const { data: subscription } = useSubscription();
  const isFreeTier = subscription?.type === FREE_SUBSCRIPTION;

  const selectedTeam = teams?.find((team) => team.id === teamId);
  const selectedTeamName = selectedTeam?.name || "Personal Workspaces";
  const selectedTeamRole = selectedTeam?.role || "team_owner";

  const { data: permissions } = usePermissions(selectedTeam?.id);
  const canCreateTeams = permissions?.canCreateTeams || false;

  const [shouldCreateTeam, setShouldCreateTeam] = useState(false);

  useEffect(() => {
    if (!isOpenTeamsModal) {
      setShouldCreateTeam(false);
    }
  }, [isOpenTeamsModal]);

  return (
    <>
      <TeamSelector teamName={selectedTeamName} teamRole={ROLE_NAMES[selectedTeamRole as keyof typeof ROLE_NAMES]}>
        <TeamMenuItem
          primaryLabel={"Personal Workspaces"}
          secondaryLabel={"Owner"}
          isSelected={!teamId}
          onClick={() => {
            navigateToPersonalWorkspace();
          }}
        />
        {teams?.map((team) => (
          <TeamMenuItem
            key={team.id}
            primaryLabel={team.name}
            secondaryLabel={ROLE_NAMES[team.role as keyof typeof ROLE_NAMES]}
            isSelected={team.id === teamId}
            onClick={() => {
              navigateToTeam(team.id);
            }}
          />
        ))}
        <MenuDivider />
        <MenuItem
          onClick={() => {
            if (isFreeTier) {
              onOpenUpsellModal();
              return;
            }
            if (permissions?.canManageTeams || teams?.length) {
              analytics.capture(goto_teams_event);
              setShouldCreateTeam(true);
              onOpenTeamsModal();
            } else {
              onOpenUpsellModal();
            }
          }}
          leftIcon={<Icon name="add" />}
          primaryLabel="New team"
        />
        {teamId && (
          <MenuItem
            onClick={() => {
              if (permissions?.canManageTeams || teams?.length) {
                analytics.capture(goto_teams_event);
                onOpenTeamsModal();
              } else {
                onOpenUpsellModal();
              }
            }}
            leftIcon={<Icon name="setting-2" />}
            primaryLabel="Manage team"
          />
        )}
      </TeamSelector>
      <UpsellModal
        title="Team collaboration"
        descriptions={[
          "Share your workspace with team members.",
          "Upgrade your Prompt Studio account to Pro plan to invite collaborators and create Promptbooks as a team.",
          "Plus, enjoy...",
        ]}
        features={PRO_SUBSCRIPTION_PERKS}
        Image={<Illustrations.ImageTeamCollaboration />}
        isOpen={isOpenUpsellModal}
        onClose={onCloseUpsellModal}
      />
      <TeamsModal
        canCreateTeams={canCreateTeams}
        isOpen={isOpenTeamsModal}
        onClose={onCloseTeamsModal}
        teamId={teamId}
        shouldCreateTeam={shouldCreateTeam}
      />
    </>
  );
};
